import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDagen, fmtDag, verschilDagen } from '@ep/core';
import { useData, useIk } from '../lib/data';
import { Laden, MultiSelect, Veld } from '../ui/ui';

/** Alle lopende projecten op een tijdas (3/6/12 maanden), filters op PM (standaard de ingelogde PM) en klant. */
export function Tijdlijn() {
  const { data: d } = useData();
  const ik = useIk().data;
  const nav = useNavigate();
  const [maanden, setMaanden] = useState(6);
  const [start, setStart] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [zoek, setZoek] = useState('');
  const [afgerond, setAfgerond] = useState(false);
  const [pm, setPm] = useState<string[] | null>(null);
  const [klant, setKlant] = useState<string[]>([]);

  const pmOpties = useMemo(() => [...new Set((d?.projecten ?? []).map((p) => p.pmNaam).filter(Boolean) as string[])].sort(), [d]);
  const pmWaarde = pm ?? (ik?.rol === 'PROJECTMANAGER' && pmOpties.includes(ik.naam) ? [ik.naam] : []);
  const eind = useMemo(() => {
    const [y, m] = start.split('-').map(Number);
    const e = new Date(Date.UTC(y, m - 1 + maanden, 1));
    return addDagen(e.toISOString().slice(0, 10), -1);
  }, [start, maanden]);
  if (!d) return <Laden />;
  const dagen = verschilDagen(start, eind) + 1;
  const pct = (x: string) => Math.max(0, Math.min(100, (verschilDagen(start, x) / dagen) * 100));
  const z = zoek.toLowerCase();
  const projecten = d.projecten
    .filter((p) => afgerond || p.status !== 'AFGEROND')
    .filter((p) => !z || [p.nummer, p.naam, p.klant].some((x) => x?.toLowerCase().includes(z)))
    .filter((p) => !pmWaarde.length || pmWaarde.includes(p.pmNaam ?? ''))
    .filter((p) => !klant.length || klant.includes(p.klant ?? ''))
    .sort((a, b) => (a.start ?? '9').localeCompare(b.start ?? '9'));
  const maandenLijst: string[] = [];
  for (let i = 0; i < maanden; i++) {
    const [y, m] = start.split('-').map(Number);
    maandenLijst.push(new Date(Date.UTC(y, m - 1 + i, 1)).toISOString().slice(0, 10));
  }
  const schuif = (n: number) => {
    const [y, m] = start.split('-').map(Number);
    setStart(new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10));
  };

  return (
    <div className="pagina kolom" style={{ gap: 12 }}>
      <div className="paneel">
        <div className="rij wrap" style={{ alignItems: 'flex-end' }}>
          <h1 style={{ marginRight: 16 }}>Tijdlijn</h1>
          <button className="knop" onClick={() => schuif(-1)}>◀</button>
          <button className="knop" onClick={() => setStart(`${d.vandaag.slice(0, 8)}01`)}>Vandaag</button>
          <button className="knop" onClick={() => schuif(1)}>▶</button>
          {[3, 6, 12].map((m) => (
            <button key={m} className={`knop ${maanden === m ? 'aan' : ''}`} onClick={() => setMaanden(m)}>{m} mnd</button>
          ))}
          <Veld label="Zoeken"><input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Project of klant" style={{ width: 200 }} /></Veld>
          <div style={{ width: 240 }}><Veld label="Projectmanager"><MultiSelect opties={pmOpties.map((x) => ({ waarde: x, label: x }))} waarden={pmWaarde} onChange={setPm} placeholder="Alle PM's" /></Veld></div>
          <div style={{ width: 240 }}><Veld label="Klant"><MultiSelect opties={[...new Set(d.projecten.map((p) => p.klant).filter(Boolean) as string[])].sort().map((x) => ({ waarde: x, label: x }))} waarden={klant} onChange={setKlant} placeholder="Alle klanten" /></Veld></div>
          <label className="check"><input type="checkbox" checked={afgerond} onChange={(e) => setAfgerond(e.target.checked)} /> Afgeronde tonen</label>
        </div>
      </div>
      <div className="paneel gantt">
        <div className="gantt-rij">
          <div className="gantt-label" />
          <div className="gantt-baan gantt-schaal">
            {maandenLijst.map((m) => (
              <div key={m} className="tik" style={{ left: `${pct(m)}%` }}>{new Date(m).toLocaleDateString('nl-NL', { month: 'short', year: '2-digit' })}</div>
            ))}
          </div>
        </div>
        {projecten.map((p) => {
          const taken = d.taken.filter((t) => t.projectId === p.id && t.startdatum && t.status === 'GEPLAND');
          const ps = p.start ?? taken.map((t) => t.startdatum!).sort()[0];
          const pe = p.oplevering ?? p.livegang ?? taken.map((t) => t.einddatum ?? t.startdatum!).sort().slice(-1)[0];
          const laat = p.prognose && p.livegang && p.prognose > p.livegang;
          return (
            <div key={p.id} className="gantt-rij" style={{ cursor: 'pointer' }} onClick={() => nav(`/projecten/${p.id}`)}>
              <div className="gantt-label" title={`${p.nummer} ${p.naam} – ${p.klant ?? ''}`}>
                <b>{p.nummer}</b> {p.naam}
                <div className="klein mute">{p.klant}{p.pmNaam ? ` · ${p.pmNaam}` : ''}</div>
              </div>
              <div className="gantt-baan" style={{ height: 34 }}>
                <div className="gantt-lijn" style={{ left: `${pct(d.vandaag)}%` }} />
                {ps && pe && pe >= start && ps <= eind && <div className="gantt-staaf project" style={{ left: `${pct(ps)}%`, width: `${Math.max(0.5, pct(pe) - pct(ps))}%` }} title={`${fmtDag(ps, 'jaar')} – ${fmtDag(pe, 'jaar')}`} />}
                {taken.filter((t) => (t.einddatum ?? t.startdatum!) >= start && t.startdatum! <= eind).map((t) => (
                  <div key={t.id} className="gantt-staaf" style={{ left: `${pct(t.startdatum!)}%`, width: `${Math.max(0.3, ((verschilDagen(t.startdatum!, t.einddatum ?? t.startdatum!) + 1) / dagen) * 100)}%`, top: 10, height: 10 }} title={`${t.titel} · ${fmtDag(t.startdatum)}`} />
                ))}
                {p.livegang && p.livegang >= start && p.livegang <= eind && <div className="gantt-ruit" style={{ left: `calc(${pct(p.livegang)}% - 6px)`, top: 8, background: laat ? 'var(--rood)' : undefined }} title={`Livegang ${fmtDag(p.livegang, 'jaar')}${p.prognose ? ` · prognose ${fmtDag(p.prognose, 'jaar')}` : ''}`} />}
              </div>
            </div>
          );
        })}
        {!projecten.length && <div className="leeg">Geen projecten in deze selectie.</div>}
      </div>
    </div>
  );
}
